import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Layers, StickyNote, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import { ACCOUNT_TIER_CONFIG } from "./StatusBadge";
import { useBulkUpdateAccounts } from "@/hooks/useTargetUniverse";
import { useAuth } from "@/contexts/AuthContext";

interface BulkActionsBarProps {
  selectedIds: Set<string>;
  onClearSelection: () => void;
}

const TIER_OPTIONS = Object.entries(ACCOUNT_TIER_CONFIG)
  .filter(([key]) => key !== "unreviewed" && key !== "disqualified")
  .map(([key, cfg]) => ({
    value: key,
    label: cfg.label,
  }));

// Quick-pick disqualification reasons. Clicking one pre-fills the editable
// textarea; reps can still type their own. Reason is required before Apply.
const DISQUALIFY_PRESETS = [
  "Not in my territory",
  "Wrong modality",
  "Competitor locked",
  "No budget",
  "Not life sciences",
  "Acquired",
  "Out of business",
];

export function BulkActionsBar({ selectedIds, onClearSelection }: BulkActionsBarProps) {
  const { userProfile } = useAuth();
  const bulkUpdate = useBulkUpdateAccounts();

  const [bulkTier, setBulkTier] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [bulkDisqualifyReason, setBulkDisqualifyReason] = useState("");
  const [showDisqualifyInput, setShowDisqualifyInput] = useState(false);

  const count = selectedIds.size;
  if (count === 0) return null;

  const performedBy = {
    id: userProfile?.id || null,
    name: userProfile?.full_name || userProfile?.sales_rep_name || "User",
  };

  const handleBulkReclassify = async () => {
    if (!bulkTier) return;
    try {
      await bulkUpdate.mutateAsync({
        accountIds: Array.from(selectedIds),
        fields: {
          account_tier: bulkTier,
          tier_changed_at: new Date().toISOString(),
          tier_changed_by: performedBy.name,
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: performedBy.name,
        },
        activityType: "status_changed",
        activityDescription: `Bulk reclassified to ${bulkTier} (${count} accounts)`,
        performedBy,
      });
      toast.success(`${count} accounts reclassified to ${bulkTier}`);
      setBulkTier("");
      onClearSelection();
    } catch {
      toast.error("Bulk reclassify failed");
    }
  };

  const handleBulkNote = async () => {
    const trimmed = bulkNote.trim();
    if (!trimmed) return;
    try {
      await bulkUpdate.mutateAsync({
        accountIds: Array.from(selectedIds),
        fields: {
          notes: trimmed,
          notes_updated_at: new Date().toISOString(),
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: performedBy.name,
        },
        activityType: "note_added",
        activityDescription: `Bulk note: ${trimmed.slice(0, 80)}${trimmed.length > 80 ? "…" : ""} (${count} accounts)`,
        performedBy,
      });
      toast.success(`Note added to ${count} accounts`);
      setBulkNote("");
      setShowNoteInput(false);
      onClearSelection();
    } catch {
      toast.error("Bulk note failed");
    }
  };

  const handleBulkDisqualify = async () => {
    const reason = bulkDisqualifyReason.trim();
    if (!reason) return;
    const now = new Date().toISOString();
    try {
      await bulkUpdate.mutateAsync({
        accountIds: Array.from(selectedIds),
        fields: {
          account_tier: "disqualified",
          qualification_reason: reason,
          tier_changed_at: now,
          tier_changed_by: performedBy.name,
          last_reviewed_at: now,
          last_reviewed_by: performedBy.name,
        },
        activityType: "status_changed",
        activityDescription: `Bulk disqualified (${count} accounts): ${reason.slice(0, 80)}${reason.length > 80 ? "…" : ""}`,
        performedBy,
      });
      toast.success(`${count} accounts disqualified`);
      setBulkDisqualifyReason("");
      setShowDisqualifyInput(false);
      onClearSelection();
    } catch {
      toast.error("Bulk disqualify failed");
    }
  };

  const isLoading = bulkUpdate.isPending;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-xl shadow-2xl px-5 py-3 flex items-center gap-3 max-w-3xl">
      <span className="text-sm font-medium whitespace-nowrap">
        {count} selected
      </span>

      {/* Reclassify */}
      <div className="flex items-center gap-1.5">
        <Layers className="h-4 w-4 text-muted-foreground" />
        <Select value={bulkTier} onValueChange={setBulkTier}>
          <SelectTrigger className="h-8 w-[150px] text-xs">
            <SelectValue placeholder="Reclassify…" />
          </SelectTrigger>
          <SelectContent>
            {TIER_OPTIONS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="secondary"
          className="h-8 text-xs"
          disabled={!bulkTier || isLoading}
          onClick={handleBulkReclassify}
        >
          {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
        </Button>
      </div>

      {/* Note toggle */}
      {!showNoteInput ? (
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1"
          onClick={() => setShowNoteInput(true)}
        >
          <StickyNote className="h-3 w-3" /> Note
        </Button>
      ) : (
        <div className="flex items-center gap-1.5">
          <Textarea
            value={bulkNote}
            onChange={(e) => setBulkNote(e.target.value)}
            placeholder="Append note…"
            className="h-8 min-h-[32px] w-[200px] text-xs resize-none py-1.5"
          />
          <Button
            size="sm"
            variant="secondary"
            className="h-8 text-xs"
            disabled={!bulkNote.trim() || isLoading}
            onClick={handleBulkNote}
          >
            Save
          </Button>
        </div>
      )}

      {/* Disqualify */}
      {!showDisqualifyInput ? (
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          onClick={() => setShowDisqualifyInput(true)}
        >
          <Ban className="h-3 w-3" /> Disqualify
        </Button>
      ) : (
        <div className="flex items-center gap-1.5">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-1 max-w-[260px]">
              {DISQUALIFY_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setBulkDisqualifyReason(preset)}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  {preset}
                </button>
              ))}
            </div>
            <Textarea
              value={bulkDisqualifyReason}
              onChange={(e) => setBulkDisqualifyReason(e.target.value)}
              placeholder="Reason required — pick above or type your own…"
              className="h-8 min-h-[32px] w-[260px] text-xs resize-none py-1.5"
            />
          </div>
          <Button
            size="sm"
            variant="destructive"
            className="h-8 text-xs"
            disabled={!bulkDisqualifyReason.trim() || isLoading}
            onClick={handleBulkDisqualify}
          >
            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : `Disqualify ${count}`}
          </Button>
        </div>
      )}

      {/* Clear */}
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 ml-1"
        onClick={onClearSelection}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
